<template>
  <div class="">
    <v-tabs-items :value="+addPlugins">
      <v-tab-item transition="tab-slide" reverse-transition="tab-slide-reverse">
        <template v-if="!itemsLoading && !items.length && !searchTerm">
          <v-container class="px-0">
            <v-row class="mx-0">
              <v-card class="mt-12 w-100" elevation="4">
                <TableFullScreenMessage
                  maxWidth="410px"
                  :title="$t('message.emptyTable.plugin.title')"
                  :desc="$t('message.emptyTable.plugin.description')"
                >
                  <template v-slot:image>
                    <pluginIllustrationMain/>
                  </template>
                  <template v-slot:action>
                    <v-btn
                      :large="!$vuetify.breakpoint.smAndDown"
                      :small="$vuetify.breakpoint.smAndDown"
                      elevation="0"
                      color="primary"
                      @click="addPlugins = true"
                      class="p-2 ml-0 add-new-button"
                    >
                      <v-icon :class="$vuetify.breakpoint.mobile ? 'mr-0' : ''"
                        >$plus</v-icon
                      >
                      {{ $vuetify.breakpoint.mobile ? "" : $t('button.addNew') }}
                    </v-btn>
                  </template>
                </TableFullScreenMessage>
              </v-card>
            </v-row>
          </v-container>
        </template>
        <template v-else>
          <v-container class="page-title">
            <v-row>
              <v-col class="page-title__title-box">
                <h4 class="page-title__title">{{ $t('heading.instance.plugin.title') }}</h4>
                <list-grid-switch
                  v-if="!$vuetify.breakpoint.mdAndDown"
                  :listStyle.sync="listStyle"
                ></list-grid-switch>
              </v-col>
              <v-col class="page-title__filter-controls">
                <data-iterator-filter-controls
                  :sortBy="listStyle ? undefined : sortBy"
                  :sortDesc="sortDesc"
                  :keys="headers"
                  :showSearchTerm="true"
                  :searchTerm="searchTerm"
                  :fullWidthSearch="true"
                  @update:sortBy="changeSortBy"
                  @update:sortDesc="handleSortDescChange"
                  @update:searchTerm="handleSearchTermChange"
                ></data-iterator-filter-controls>
                <v-btn
                  :large="!$vuetify.breakpoint.smAndDown"
                  :small="$vuetify.breakpoint.smAndDown"
                  elevation="0"
                  color="primary"
                  class="p-2 add-new-button page-title__add-button"
                  @click="addPlugins = true"
                >
                  <v-icon :class="$vuetify.breakpoint.mobile ? 'mr-0' : ''"
                    >$plus</v-icon
                  >
                  {{ $vuetify.breakpoint.mobile ? "" : $t('button.addNew') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-container>

          <template v-if="listStyle === 0 || $vuetify.breakpoint.mdAndDown">
            <data-iterator
              :searchTerm="searchTerm"
              :itemsTotal="itemsTotal"
              :items="itemsLoading ? [] : items"
              :keys="headers"
              :sortBy="sortBy"
              :sortDesc="sortDesc"
              :possibleItemsPerPage="[[8, 16, 24]]"
              :itemsPerPage="itemsPerPage"
              :page="page"
              :loading="itemsLoading"
              @clearAllFilters="handleSearchTermChange('')"
              @update:page="changePage"
              @update:itemsPerPage="changePerPage"
            >
              <template v-slot:loading>
                <v-row class="">
                  <v-col
                    v-for="(item, key) in 8"
                    :key="key"
                    cols="12"
                    xs="12"
                    sm="6"
                    md="6"
                    xl="3"
                    class="responsive-card"
                  >
                    <plugin-card-skeleton />
                  </v-col>
                </v-row>
              </template>

              <template v-slot:grid="{ items: items }">
                <v-col
                  cols="12"
                  xs="12"
                  sm="6"
                  md="6"
                  xl="3"
                  class="responsive-card"
                  v-for="(item, key) in items"
                  :key="key"
                >
                  <plugin-card
                    :item="item"
                    :checked="checkedItems.includes(item.name)"
                    @checked="handleItemCheck(item)"
                    @action-button-activate="showPluginActivateModal"
                    @action-button-deactivate="showPluginDeactivateModal"
                    @action-button-update="showPluginUpdateModal"
                    @action-button-delete="showPluginDeleteModal($event)"
                    @action-button-read-more="readMoreActive = $event"
                  ></plugin-card>
                </v-col>
              </template>
            </data-iterator>
          </template>

          <!-- TABLE VIEW -->

          <template v-if="listStyle === 1 && !$vuetify.breakpoint.mdAndDown">
            <plugin-table
              :checkedItems="checkedItems"
              :headers="headers"
              :items="items"
              :itemsLoading="itemsLoading"
              :itemsPerPage="itemsPerPage"
              :itemsTotal="itemsTotal"
              :page="page"
              :possibleItemsPerPage="[[8, 16, 24]]"
              :searchTerm="searchTerm"
              :sortBy="sortBy"
              :sortDesc="sortDesc"
              @update:check-all="handleSelectAll"
              @update:checked-items="handleItemCheck"
              @update:itemsPerPage="changePerPage"
              @update:page="changePage"
              @update:sortBy="changeSortBy"
              @update:sortDesc="changeSortDesc"
              @update:searchTerm="handleSearchTermChange($event)"
              @action-button-activate="showPluginActivateModal"
              @action-button-deactivate="showPluginDeactivateModal"
              @action-button-update="showPluginUpdateModal"
              @action-button-delete="showPluginDeleteModal($event)"
              @action-button-read-more="readMoreActive = $event"
            >
            </plugin-table>
          </template>
        </template>
      </v-tab-item>
      <v-tab-item transition="tab-slide" reverse-transition="tab-slide-reverse">
        <v-container class="page-title">
          <v-row>
            <v-col class="page-title__title-box">
              <v-btn @click="addPlugins = false" icon small class="mr-2">
                <v-icon size="24">$arrowleft</v-icon>
              </v-btn>
              <h4 class="page-title__title">{{ $t('heading.instance.plugin.add.title') }}</h4>
              <list-grid-switch
                v-if="!$vuetify.breakpoint.mdAndDown"
                :listStyle.sync="listStyle"
              ></list-grid-switch>
            </v-col>
            <v-col class="page-title__filter-controls">
              <data-iterator-filter-controls
                :disabled="!newPlugins.searchTerm"
                :showSearchTerm="false"
                :sortBy="!listStyle ? newPlugins.sortBy : undefined"
                :keys="!listStyle ? newPlugins.keys : undefined"
                @update:sortBy="newPlugins.sortBy = $event"
              ></data-iterator-filter-controls>
            </v-col>
          </v-row>
        </v-container>

        <v-container class="px-0 mb-6">
          <v-row class="mx-0">
            <NewItemSearchCard
              :isActive="addPlugins ? true : false"
              :item="newPlugins"
              @update:searchTerm="newPluginsHandleSearchTermChange($event)"
            />
          </v-row>
        </v-container>

        <template v-if="!filteredNewPlugins.length">
          <v-container class="px-0">
            <v-row class="mx-0">
              <v-card
                class="w-100 d-flex align-center"
                style="min-height: 360px"
                elevation="4"
              >
                <TableFullScreenMessage
                  class="my-auto"
                  maxWidth="410px"
                  :title="$t('heading.instance.plugin.add.empty.title')"
                  :desc="$t('heading.instance.plugin.add.empty.text')"
                >
                  <template v-slot:image>
                    <pluginsIllustration/>
                  </template>
                  <template v-slot:action>
                    <v-btn
                      @click="addPlugins = false"
                      outlined
                      large
                      block
                      color="gray lighten-3"
                    >
                      <span class="gray--text text--darken-1">{{ $t('button.back') }}</span>
                    </v-btn>
                  </template>
                </TableFullScreenMessage>
              </v-card>
            </v-row>
          </v-container>
        </template>
        <template v-else>
          <template v-if="$vuetify.breakpoint.mdAndDown || listStyle === 0">
            <data-iterator
              class="py-0"
              :items="filteredNewPlugins"
              :loading="newPlugins.itemsLoading"
              :itemsPerPage="newPlugins.itemsPerPage"
              :itemsTotal="newPlugins.itemsTotal"
              :keys="newPlugins.headers"
              :sortBy="newPlugins.sortBy"
              :sortDesc="true"
              :possibleItemsPerPage="[[8, 16, 24]]"
              :page="newPlugins.page"
              @update:page="newPluginsChangePage"
              @update:itemsPerPage="newPluginsChangePerPage"
            >
              <template v-slot:no-data v-if="!newPlugins.itemsLoading">
                <template v-if="newPlugins.searchTerm">
                  <data-iterator-no-results-container
                    @clearAllFilters="newPlugins.searchTerm = ''"
                    :searchTerm="newPlugins.searchTerm"
                  />
                </template>
              </template>
              <template v-slot:loading>
                <v-row class="mt-0">
                  <v-col
                    v-for="(_, key) in 8"
                    :key="key"
                    cols="12"
                    xs="12"
                    sm="6"
                    md="6"
                    xl="3"
                    class="responsive-card"
                  >
                    <plugin-card-skeleton />
                  </v-col>
                </v-row>
              </template>
              <template v-slot:grid="{ items: items }">
                <v-col
                  cols="12"
                  xs="12"
                  sm="6"
                  md="6"
                  xl="3"
                  v-for="(item, key) in items"
                  :key="key"
                >
                  <new-plugin-card
                    :item="item"
                    :loading="newPlugins.itemsLoading"
                    :itemIsFeatured="key === 0 ? true : false"
                    @action-button-details="showDetailsModal(item)"
                    @action-button-install="showInstallModal(item)"
                    @action-button-read-more="readMoreActive = $event"
                    @action-button-delete="showPluginDeleteModal($event)"
                  ></new-plugin-card>
                </v-col>
              </template>
            </data-iterator>
          </template>
          <!-- TABLE VIEW -->

          <template v-if="!$vuetify.breakpoint.mdAndDown && listStyle === 1">
            <new-plugin-table
              :checkedItems="[]"
              :headers="newPlugins.headers"
              :items="filteredNewPlugins"
              :itemsLoading="newPlugins.itemsLoading"
              :itemsPerPage="newPlugins.itemsPerPage"
              :itemsTotal="newPlugins.itemsTotal"
              :page="newPlugins.page"
              :possibleItemsPerPage="[[8, 16, 24]]"
              :searchTerm="newPlugins.searchTerm"
              @clearAllFilters="newPluginsHandleSearchTermChange('')"
              :sortBy.sync="newPlugins.sortBy"
              :sortDesc="newPlugins.sortDesc"
              @update:itemsPerPage="newPluginsChangePerPage"
              @update:page="newPluginsChangePage"
              @update:sortBy="newPluginsChangeSortBy"
              @update:sortDesc="newPluginsChangeSortDesc"
              @action-button-details="showDetailsModal"
              @action-button-install="showInstallModal($event)"
              @action-button-read-more="readMoreActive = $event"
            >
            </new-plugin-table>
          </template>
        </template>
      </v-tab-item>
    </v-tabs-items>
    <!-- PLUGINS -->

    <PluginReadMoreModal
      :item="readMoreActive"
      @resetModal="readMoreActive = {}"
      @installItem="showInstallModal"
      @action-button-install="showInstallModal($event)"
      @action-button-activate="showPluginActivateModal($event)"
      @action-button-deactivate="showPluginDeactivateModal($event)"
      @action-button-update="showPluginUpdateModal($event)"
      @action-button-delete="showPluginDeleteModal($event)"
    />

    <v-dialog
      :persistent="modalOptions.persistent"
      v-model="modalOptions.open"
      ref="dialog"
      transition="custom-dialog-transition"
    >
      <div
        class="card-overlay"
        v-if="!modalOptions.persistent"
        @click="modalOptions.open = !modalOptions.open"
      />
      <div class="card-overlay" v-else @click="$refs.dialog.animateClick()" />
      <basic-modal
        style="width: 560px"
        :modalOptions="modalOptions"
        @modal-close="modalClose"
        :key="modalRender"
      >
        <template v-slot:custom="{ item }">
          <template v-if="item">
            <template v-if="Array.isArray(item)">
              <div class="target-item-box flex-column">
                <div
                  v-for="(subitem, index) in item"
                  :key="index"
                  class="d-flex"
                  :class="item.length - 1 !== index ? 'mb-3' : ''"
                >
                  <span
                    class="gray--text text--darken-1"
                    v-html="subitem.title || subitem.name"
                  ></span>

                  <span
                    class="ml-auto"
                    style="min-width: 48px"
                    v-if="subitem.rating"
                  >
                    <v-icon
                      size="20"
                      class="mr-1"
                      :percent="subitem.rating / 100"
                    >
                      $starIcon
                    </v-icon>
                    <b class="p-4">{{
                      Math.ceil((subitem.rating / 10) * 5) / 10
                    }}</b>
                  </span>
                </div>
              </div>
            </template>

            <div class="target-item-box" v-else-if="Object.keys(item).length">
              <span
                class="gray--text text--darken-1"
                v-html="item.title || item.name"
              ></span>
              <span class="ml-auto" style="min-width: 48px" v-if="item.rating">
                <v-icon size="20" class="mr-1" :percent="item.rating / 100">
                  $starIcon
                </v-icon>
                <b class="p-4">{{ Math.ceil((item.rating / 10) * 5) / 10 }}</b>
              </span>
            </div>
          </template>
        </template>
      </basic-modal>
    </v-dialog>
    <plugins-select-snackbar
      :items="items"
      :checkedItems="checkedItems"
      :checkedPlugins="getCheckedItems()"
      @update:check-all="handleSelectAll"
      @mass-action-remove="showPluginMassDeleteModal(getCheckedItems())"
      @mass-action-activate="showPluginMassActivateModal(getCheckedItems())"
      @mass-action-deactivate="showPluginMassDeactivateModal(getCheckedItems())"
      @mass-action-update="showPluginMassUpdateModal(getCheckedItems())"
    />
  </div>
</template>

<script>
import {
  fetchActiveInstancePlugins,
  fetchNewPlugins,
  fetchNewPluginsFromPackages,
} from "../../router/fetchData";

import DataIteratorPageMixin from "../../mixins/DataIteratorPageMixin";
import CustomTablePageMixin from "../../mixins/CustomTablePageMixin";
import ActionModalMixin from "../../mixins/ActionModalMixin";
import PluginActionsMixin from "../../mixins/PluginsActionsMixin";

import PluginTable from "../../components/plugins/PluginTable.vue";
import NewPluginTable from "../../components/plugins/NewPluginTable.vue";
import ListGridSwitch from "../../components/buttons/ListGridSwitch.vue";
import DataIterator from "../../components/dataIterator/DataIterator.vue";
import PluginCard from "../../components/plugins/PluginCard.vue";
import NewPluginCard from "../../components/plugins/NewPluginCard.vue";
import DataIteratorFilterControls from "../../components/dataIterator/DataIteratorFilterControls.vue";
import PluginCardSkeleton from "../../components/plugins/PluginCardSkeleton.vue";
import PluginsSelectSnackbar from "../../components/plugins/PluginsSelectSnackbar.vue";
import BasicModal from "@/components/modal/BasicModal";
import TableFullScreenMessage from "../../components/dataIterator/tables/TableFullScreenMessage.vue";
import DataIteratorNoResultsContainer from "../../components/dataIterator/DataIteratorNoResultsContainer.vue";
import NewItemSearchCard from "../../components/dataIterator/NewItemSearchCard.vue";
import PluginReadMoreModal from "../../components/plugins/PluginReadMoreModal.vue";
import pluginsIllustration from "../../components/illustrations/plugins-illustration.vue";
import pluginIllustrationMain from "../../components/illustrations/plugin-illustration-main.vue";

export default {
  components: {
    PluginTable,
    NewPluginTable,
    ListGridSwitch,
    DataIterator,
    PluginCard,
    NewPluginCard,
    DataIteratorFilterControls,
    BasicModal,
    PluginCardSkeleton,
    PluginsSelectSnackbar,
    TableFullScreenMessage,
    DataIteratorNoResultsContainer,
    NewItemSearchCard,
    PluginReadMoreModal,
    pluginsIllustration,
    pluginIllustrationMain,
  },
  props: {
    instance: Object,
  },
  data() {
    return {
      readMoreActive: {},
      fetching: true,
      addPlugins: false,
      /*
       * installed plugins
       */
      headers: [
        {
          text: "",
          value: "select",
          sortable: false,
        },
        {
          text: 'name',
          align: "start",
          value: "title",
          sortable: true,
          searchable: true,
        },
        {
          text: 'version',
          value: "version",
          sortable: false,
        },
        {
          text: 'status',
          sortable: true,
          value: "status",
        },
        {
          text: "",
          value: "actions",
          sortable: false,
          align: "end",
        },
      ],
      sortDesc: false,
      sortBy: {
        text: 'name',
        align: "start",
        value: "title",
        sortable: true,
        searchable: true,
      },
      items: [],
      allItems: [],
      page: 1,
      itemsPerPage: 8,
      itemsLoading: true,
      searchTerm: "",
      itemsTotal: 0,

      /**
       * new plugins
       */
      newPlugins: {
        keys: [
          {
            text: 'sort.noSort',
            value: "",
            sortable: true,
          },
          {
            text: 'sort.popular',
            value: "active_installs",
            sortable: true,
          },
          {
            text: 'sort.downloadable',
            value: "active_installs",
            sortable: true,
          },
          {
            text: 'sort.rated',
            value: "rating",
            sortable: true,
          },
          {
            text: 'sort.recent',
            value: "last_updated",
            sortable: true,
          },
        ],
        headers: [
          {
            text: 'name',
            align: "start",
            value: "name",
            sortable: true,
          },
          {
            text: 'rating',
            value: "rating",
            sortable: true,
          },
          {
            text: 'version',
            value: "version",
            sortable: false,
          },
          {
            text: 'installations',
            value: "active_installs",
            sortable: true,
          },
          {
            text: 'requires',
            sortable: true,
            value: "tested",
          },
          {
            text: 'lastUpdated',
            value: "last_updated",
            sortable: true,
          },
          {
            text: "",
            value: "actions",
            sortable: false,
            align: "end",
          },
        ],
        sortDesc: false,
        sortBy: {
          text: 'noSort',
          value: "",
          sortable: true,
        },
        items: [],
        allItems: [],
        page: 1,
        itemsPerPage: 8,
        itemsLoading: false,
        searchTerm: "",
        itemsTotal: 0,
      },
      /**
       * modal
       */
      modalRender: 0,
      modalOptions: {},

      applicationId: this.$route.params.id,
    };
  },
  mixins: [
    DataIteratorPageMixin,
    CustomTablePageMixin,
    PluginActionsMixin,
    ActionModalMixin,
  ],
  mounted() {
    this.$root.$on("plugin-item-changed", (plugin) => {
      this.instance.setPluginItem(plugin);
      this.updatePluginStatus(plugin);
    });
    this.loadData();

    this.reloadNewPlugins();
  },
  watch: {
    searchTerm: function () {
      this.$nextTick(() => {
        this.checkedItems = this.checkedItems.filter((item) => {
          return this.items.find((i) => i.name == item);
        });
      });
    },

    addPlugins: function (value) {
      if (value) {
        this.checkedItems = [];
        this.newPlugins.searchTerm = "";
        this.$nextTick(() => {
          this.reloadNewPlugins();
        });
      }
    },
    "modalOptions.open": function (value) {
      value
        ? this.$store.dispatch("lockBodyScroll")
        : this.$store.dispatch("unlockBodyScroll");
    },
  },
  methods: {
    getCheckedItems() {
      return this.items.filter((item) => {
        return this.checkedItems.includes(item.name);
      });
    },
    updatePluginStatus(plugin) {
      for (let [key, item] of this.items.entries()) {
        if (item.name == plugin.name) {
          this.items[key] = this.transformItem(plugin);
          this.items = [...this.items];
          break;
        }
      }
      for (let item of this.newPlugins.items) {
        if (item.slug == plugin.name || item.slug == plugin.slug) {
          item.async_status = plugin.async_status;
          this.newPlugins.items = [...this.newPlugins.items];
          break;
        }
      }
    },
    transformItem(item) {
      if (typeof item.async_status === "undefined") {
        item.async_status = {};
      }
      item.hasInstallInProgress = () => {
        return (
          item.async_status &&
          (item.async_status.install == "pending" ||
            item.async_status.install == "in_progress")
        );
      };
      item.hasUpdateInProgress = () => {
        return (
          item.async_status &&
          (item.async_status.update == "pending" ||
            item.async_status.update == "in_progress")
        );
      };
      return item;
    },
    setData(data) {
      this.allItems = data.map((item) => {
        return this.transformItem(item);
      });
      this.loadPlugins();
      this.$emit("plugins-reload", this.allItems);
    },
    loadData() {
      fetchActiveInstancePlugins(this.applicationId).then((response) => {
        this.itemsLoading = false;
        if (response.error) {
          // console.error("ERROR: ", response.error);
          return;
        }
        this.allItems = response.data.map((item) => {
          return this.transformItem(item);
        });
        this.fetching = false;
        this.loadPlugins();
        this.$emit("plugins-reload", this.allItems);
      });
    },
    loadPlugins() {
      this.itemsLoading = true;
      let items = [...this.allItems];
      // filtering
      if (this.searchTerm) {
        items = items.filter((item) => {
          for (const header of this.headers) {
            if (header.searchable) {
              if (
                item[header.value]
                  .toLowerCase()
                  .includes(this.searchTerm.toLowerCase())
              ) {
                return true;
              }
            }
          }
          return false;
        });
      }
      this.itemsTotal = items.length;
      // sorting
      items = items.sort((i1, i2) => {
        if (!isNaN(i1[this.sortBy.value]) && !isNaN(i2[this.sortBy.value])) {
          return (
            (i1[this.sortBy.value] - i2[this.sortBy.value]) *
            (this.sortDesc ? -1 : 1)
          );
        }
        return (
          i1[this.sortBy.value]
            .toString()
            .localeCompare(i2[this.sortBy.value].toString()) *
          (this.sortDesc ? -1 : 1)
        );
      });
      // paging
      items = items.slice(
        (this.page - 1) * this.itemsPerPage,
        this.page * this.itemsPerPage
      );
      this.items = items;
      this.itemsLoading = false;
    },
    changePage(page) {
      this.page = page;
      this.loadPlugins();
    },
    changePerPage(perPage) {
      this.page = 1;
      this.itemsPerPage = perPage;
      this.loadPlugins();
    },
    changeSortBy: function (data) {
      this.page = 1;
      this.sortBy = data;
      this.loadPlugins();
    },
    changeSortDesc: function (data) {
      this.page = 1;
      this.sortDesc = data;
      this.loadPlugins();
    },
    handleSearchTermChange: function (newValue) {
      this.page = 1;
      this.searchTerm = newValue;
      this.loadPlugins();
    },

    /**
     * new plugins
     */
    reloadNewPlugins() {
      this.newPlugins.itemsLoading = true;
      if (this.newPlugins.searchTerm) {
        fetchNewPlugins(this.applicationId, this.newPlugins.searchTerm).then(
          (response) => {
            this.newPlugins.itemsLoading = false;
            if (response.error) {
              // console.error("ERROR: ", response.error);
              return;
            }
            this.newPlugins.allItems = response.data.map((item) => {
              return this.transformItem(item);
            });
            this.loadNewPlugins();
          }
        );
      } else {
        fetchNewPluginsFromPackages(this.applicationId).then((response) => {
          this.newPlugins.itemsLoading = false;
          if (response.error) {
            // console.error("ERROR: ", response.error);
            return;
          }
          this.newPlugins.allItems = response.data.map((item) => {
            item.featured = true;
            return this.transformItem(item);
          });
          this.loadNewPlugins();
        });
      }
    },
    loadNewPlugins() {
      this.newPlugins.itemsLoading = true;
      let items = [...this.newPlugins.allItems];
      this.newPlugins.itemsTotal = items.length;

      // sorting

      var sortBy = this.newPlugins.sortBy.value;
      var sortDesc = this.newPlugins.sortDesc;

      items = items.sort((i1, i2) => {
        if (!isNaN(i1[sortBy]) && !isNaN(i2[sortBy])) {
          return (i1[sortBy] - i2[sortBy]) * (sortDesc ? -1 : 1);
        }
        // console.log({ i1, i2 });
        // return (
        //   i1[sortBy]
        //     .toString()
        //     .localeCompare(i2[sortBy].toString()) * (sortDesc ? -1 : 1)
        // );
      });
      // paging

      items = items.slice(
        (this.newPlugins.page - 1) * this.newPlugins.itemsPerPage,
        this.newPlugins.page * this.newPlugins.itemsPerPage
      );

      this.newPlugins.items = items;
      this.newPlugins.itemsLoading = false;
    },
    newPluginsChangePage(page) {
      this.newPlugins.page = page;
      this.loadNewPlugins();
    },
    newPluginsChangePerPage(perPage) {
      this.newPlugins.page = 1;
      this.newPlugins.itemsPerPage = perPage;
      this.loadNewPlugins();
    },
    newPluginsChangeSortBy: function (data) {
      this.newPlugins.page = 1;
      this.newPlugins.sortBy = data;
      this.loadNewPlugins();
    },
    newPluginsChangeSortDesc: function (data) {
      this.newPlugins.page = 1;
      this.newPlugins.sortDesc = data;
      this.loadNewPlugins();
    },
    newPluginsHandleSearchTermChange: function (newValue) {
      this.newPlugins.searchTerm = newValue;
      this.reloadNewPlugins();
    },

    /**
     * modal
     */
    showInstallModal(item) {
      this.showPluginInstallModal(item);
    },
    showDetailsModal(item) {
      this.showPluginInstallModal(item);
    },
  },
  computed: {
    filteredNewPlugins() {
      return this.newPlugins.allItems.filter((item) => {
        return !this.allItems.find((i) => i.name === item.slug);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.v-window::v-deep {
  overflow: visible;

  .v-window__container {
    overflow: visible !important;
  }
}

.target-item-box::v-deep {
  border-radius: 6px;
  border: 1px solid var(--v-gray-lighten3);
  display: flex;
  font-size: $font-size-base;
  line-height: $line-height-base;
  padding: 16px;
  background: var(--v-gray-lighten5);
  font-weight: $font-weight-semibold;
  margin-bottom: 8px;

  + form {
    margin-top: 16px;
  }
}
</style>
